import React, { useMemo, useState, useEffect } from 'react'
import { Button, Tag, Modal, Form, Input, Select, Typography, Alert } from 'antd'
import { useQuery, useMutation, useQueryClient, keepPreviousData } from '@tanstack/react-query'
import {
  CheckOutlined,
  CloseOutlined,
} from '@ant-design/icons'
import { useNavigate } from 'react-router-dom'
import { approvalsApi } from '@/api/services'
import { Approval, UpdateApprovalRequest, BulkUpdateRequest } from '@/types'
import { toast } from 'sonner'
import { leapTablePagination } from '@/components/table/defaultPagination'
import { Button as UiButton } from '@/components/ui/Button'
import { DocumentPreviewSummary } from '@/components/approvals/DocumentPreviewSummary'
import { OTHERS_REASON, buildRemarks, reasonsFor, DecisionStatus } from '@/components/approvals/decisionReasons'
import { approvalRowClass } from '@/components/table/statusRowClass'
import {
  buildPendingActionsColumn,
  buildViewActionColumn,
  translateApprovalColumns,
} from '@/components/table/approvalTableColumns'
import { LeapDataTable } from '@/components/table/LeapDataTable'
import { isTableChromeTarget } from '@/components/table/tableChrome'
import { useTranslation } from 'react-i18next'
import { useAuthStore } from '@/stores/authStore'
import { PageShell, PageHero, DashboardPanel } from '@/components/dashboard'
import { ClipboardCheck } from 'lucide-react'

const { Text } = Typography

const { Option } = Select
const { TextArea } = Input

/** Backend caps bulk approve/reject at 10 records per request (see approvalsController.bulkUpdateApprovals). */
const MAX_BULK_UPDATE = 10
const PENDING_POD_SELECTION_KEY = 'pod:pending-pod-selection'

function readPersistedSelection(): React.Key[] {
  try {
    const raw = sessionStorage.getItem(PENDING_POD_SELECTION_KEY)
    if (!raw) return []
    const parsed = JSON.parse(raw) as unknown
    if (!Array.isArray(parsed)) return []
    return parsed.filter((id) => id != null && id !== '')
  } catch {
    return []
  }
}

function usePersistedPendingSelection() {
  const [selectedRowKeys, setSelectedRowKeys] = useState<React.Key[]>(readPersistedSelection)

  useEffect(() => {
    sessionStorage.setItem(PENDING_POD_SELECTION_KEY, JSON.stringify(selectedRowKeys))
  }, [selectedRowKeys])

  return [selectedRowKeys, setSelectedRowKeys] as const
}

const PendingPODPage: React.FC = () => {
  const navigate = useNavigate()
  const [approvalModalVisible, setApprovalModalVisible] = useState(false)
  const [decisionMode, setDecisionMode] = useState<'Approved' | 'Rejected'>('Approved')
  const [bulkModalVisible, setBulkModalVisible] = useState(false)
  const [selectedApproval, setSelectedApproval] = useState<Approval | null>(null)
  const [viewModalOpen, setViewModalOpen] = useState(false)
  const [viewUrl, setViewUrl] = useState<string | null>(null)
  const [viewRecord, setViewRecord] = useState<Approval | null>(null)
  const [form] = Form.useForm()
  const [bulkForm] = Form.useForm()
  const watchedReason = Form.useWatch('reason', form)
  const remarksRequired = watchedReason === OTHERS_REASON
  const watchedBulkStatus = Form.useWatch('status', bulkForm)
  const watchedBulkReason = Form.useWatch('reason', bulkForm)
  const bulkRemarksRequired = watchedBulkReason === OTHERS_REASON
  const { t } = useTranslation(['PendingPOD', 'Common', 'ReportsPOD'])
  const { user } = useAuthStore()
  const canApprove = user?.permissions?.includes('pod.approve') ?? false

  useEffect(() => {
    bulkForm.setFieldValue('reason', undefined)
  }, [watchedBulkStatus, bulkForm])

  const queryClient = useQueryClient()

  const [selectedRowKeys, setSelectedRowKeys] = usePersistedPendingSelection()
  const [selectedRecordsMap, setSelectedRecordsMap] = useState<Map<number, Approval>>(new Map())

  const [tableFilters, setTableFilters] = useState<Record<string, any>>({})
  const [documentNoQuery, setDocumentNoQuery] = useState('')
  const [docTypeQuery, setDocTypeQuery] = useState<string | undefined>(undefined)
  const [currentPage, setCurrentPage] = useState(1)

  useEffect(() => {
    setCurrentPage(1)
  }, [documentNoQuery, docTypeQuery])

  const { data: approvals, isLoading, isFetching } = useQuery({
    queryKey: ['approvals', { status: 'Pending', documentNo: documentNoQuery, type: docTypeQuery }],
    queryFn: () =>
      approvalsApi.getApprovals({
        status: 'Pending',
        documentNo: documentNoQuery || undefined,
        type: docTypeQuery || undefined,
        limit: 5000,
      }),
    select: (response: any) => response.data?.data,
    placeholderData: keepPreviousData,
  })

  useEffect(() => {
    if (!Array.isArray(approvals)) return
    setSelectedRecordsMap((prevMap) => {
      const newMap = new Map(prevMap)
      const selectedKeySet = new Set(selectedRowKeys.map((k) => Number(k)))

      for (const key of newMap.keys()) {
        if (!selectedKeySet.has(key)) {
          newMap.delete(key)
        }
      }

      approvals.forEach((record: Approval) => {
        if (record && selectedKeySet.has(Number(record.id))) {
          newMap.set(Number(record.id), record)
        }
      })

      return newMap
    })
  }, [approvals, selectedRowKeys])

  const updateApprovalMutation = useMutation({
    mutationFn: (args: { id: number; data: UpdateApprovalRequest }) =>
      approvalsApi.updateApproval(args.id, args.data),
    onSuccess: (_data, variables) => {
      const status = variables?.data?.status
      if (status === 'Approved') {
        toast.success(t('PendingPOD:messages.approveSuccess', 'Approved successfully'))
      } else if (status === 'Rejected') {
        toast.success(t('PendingPOD:messages.rejectSuccess', 'Rejected successfully'))
      } else {
        toast.success(t('PendingPOD:messages.updateSuccess', 'Approval updated successfully'))
      }
      if (variables?.id) {
        setSelectedRowKeys((prev) => prev.filter((k) => Number(k) !== Number(variables.id)))
      }
      setApprovalModalVisible(false)
      setSelectedApproval(null)
      form.resetFields()
      queryClient.invalidateQueries({ queryKey: ['approvals'] })
      queryClient.invalidateQueries({ queryKey: ['dashboard'] })
    },
    onError: (error: any) => {
      toast.error(error.response?.data?.error || t('PendingPOD:messages.updateFailed', 'Failed to update approval'))
    },
  })

  const bulkUpdateMutation = useMutation({
    mutationFn: (data: BulkUpdateRequest) => approvalsApi.bulkUpdateApprovals(data),
    onSuccess: () => {
      toast.success(t('PendingPOD:messages.bulkSuccess', 'Bulk update completed successfully'))
      setBulkModalVisible(false)
      setSelectedRowKeys([])
      bulkForm.resetFields()
      queryClient.invalidateQueries({ queryKey: ['approvals'] })
      queryClient.invalidateQueries({ queryKey: ['dashboard'] })
    },
    onError: (error: any) => {
      toast.error(error.response?.data?.error || t('PendingPOD:messages.bulkFailed', 'Failed to bulk update approvals'))
    },
  })

  const openDecisionModal = (record: Approval, preset: 'Approved' | 'Rejected') => {
    setSelectedApproval(record)
    setDecisionMode(preset)
    setApprovalModalVisible(true)
    // Reset the reason/remarks; the popup is dedicated to the chosen action.
    form.setFieldsValue({ reason: undefined, remarks: '' })
  }

  const handleApprovalSubmit = (values: any) => {
    if (!selectedApproval) return

    updateApprovalMutation.mutate({
      id: selectedApproval.id,
      data: {
        status: decisionMode,
        remarks: buildRemarks(values.reason, values.remarks),
      },
    })
  }

  const handleBulkUpdate = () => {
    if (selectedRowKeys.length === 0) {
      toast.error(t('PendingPOD:messages.selectAtLeastOne', 'Please select at least one approval'))
      return
    }

    setBulkModalVisible(true)
  }

  const handleBulkUpdateSubmit = (values: any) => {
    const updates = selectedRowKeys.map((id) => ({
      id: Number(id),
      status: values.status,
      reason: values.reason,
      remarks: values.remarks,
    }))

    bulkUpdateMutation.mutate({ updates })
  }

  const resolveAttachmentUrl = (record: Approval): string | null =>
    ((record as any).tempUrl as string) ||
    ((record as any).newUrl as string) ||
    ((record as any).url as string) ||
    null

  const handleViewDocument = (record: Approval) => {
    const url = resolveAttachmentUrl(record)
    if (!url) {
      toast.error(t('PendingPOD:messages.noAttachmentAvailable', 'No attachment available'))
      return
    }
    setViewRecord(record)
    setViewUrl(url)
    setViewModalOpen(true)
  }

  const goToApproval = (record: Approval) => {
    navigate(`/approvals/${record.id}`)
  }

  const [showOnlySelected, setShowOnlySelected] = useState(false)

  // Natural dataset rows (prevents selected rows on Page 4/5 from jumping to Page 1 on check)
  const displayedRows = useMemo(() => {
    const raw = Array.isArray(approvals) ? approvals : []
    if (!showOnlySelected || !selectedRowKeys.length) return raw

    const selectedKeySet = new Set(selectedRowKeys.map((k) => Number(k)))
    const combinedMap = new Map<number, Approval>()

    selectedRecordsMap.forEach((rec, id) => {
      if (selectedKeySet.has(id)) {
        combinedMap.set(id, rec)
      }
    })

    raw.forEach((r) => {
      if (selectedKeySet.has(Number(r.id))) {
        combinedMap.set(Number(r.id), r)
      }
    })

    return Array.from(combinedMap.values())
  }, [approvals, selectedRowKeys, showOnlySelected, selectedRecordsMap])

  const isPdf = useMemo(() => (viewUrl ? /\.pdf($|\?)/i.test(viewUrl) : false), [viewUrl])

  const handleTableChange = (pagination: any, filters: any, _sorter: any, extra: any) => {
    setTableFilters(filters)

    if (extra?.action === 'filter') {
      setCurrentPage(1)
    } else if (extra?.action === 'paginate') {
      setCurrentPage(pagination.current || 1)
    }

    const selectedType =
      Array.isArray(filters?.type) && filters.type.length > 0 && filters.type[0]
        ? String(filters.type[0]).trim()
        : undefined

    setDocTypeQuery(selectedType || undefined)

    const searchedDocNo = filters?.documentNo?.[0] ? String(filters.documentNo[0]).trim() : ''

    if (!searchedDocNo) {
      setDocumentNoQuery('')
      return
    }

    const rawList = Array.isArray(approvals) ? approvals : []
    const isFound = rawList.some((r: Approval) => {
      const docNoStr = String(r.documentNo ?? '')
      return docNoStr.toLowerCase().includes(searchedDocNo.toLowerCase())
    })

    if (isFound) {
      // If found in the current loaded list, use client-side filter by keeping query empty
      setDocumentNoQuery('')
    } else {
      setDocumentNoQuery(searchedDocNo)
    }
  }

  const columns = useMemo(() => {
    const baseCols = translateApprovalColumns(t)
    const mappedCols = baseCols.map((col) => {
      const keyStr = col.key ? String(col.key) : ''
      if (keyStr && keyStr in tableFilters) {
        return {
          ...col,
          filteredValue: tableFilters[keyStr] || null,
        }
      }
      return col
    })
    return [
      ...mappedCols,
      ...(canApprove
        ? [
            buildPendingActionsColumn({
              t,
              onView: handleViewDocument,
              onApprove: (record) => openDecisionModal(record, 'Approved'),
              onReject: (record) => openDecisionModal(record, 'Rejected'),
            }),
          ]
        : [
            buildViewActionColumn({
              t,
              onView: handleViewDocument,
            }),
          ]),
    ]
  }, [t, tableFilters, canApprove])

  const rowSelection = {
    selectedRowKeys,
    onChange: (newSelectedRowKeys: React.Key[], currentSelectedRows: Approval[]) => {
      let keysToUse = newSelectedRowKeys
      if (newSelectedRowKeys.length > MAX_BULK_UPDATE) {
        toast.warning(
          t('PendingPOD:messages.bulkMax', 'You can approve or reject up to {{max}} documents at once.', {
            max: MAX_BULK_UPDATE,
          })
        )
        keysToUse = newSelectedRowKeys.slice(0, MAX_BULK_UPDATE)
      }

      const keySet = new Set(keysToUse.map((k) => Number(k)))
      setSelectedRecordsMap((prevMap) => {
        const newMap = new Map(prevMap)
        for (const k of newMap.keys()) {
          if (!keySet.has(k)) {
            newMap.delete(k)
          }
        }
        currentSelectedRows.forEach((record) => {
          if (record && keySet.has(Number(record.id))) {
            newMap.set(Number(record.id), record)
          }
        })
        return newMap
      })

      setSelectedRowKeys(keysToUse)
    },
    getCheckboxProps: (record: Approval) => ({
      // Disable un-selected rows once the cap is reached so the limit is obvious.
      disabled:
        record.status !== 'Pending' ||
        (selectedRowKeys.length >= MAX_BULK_UPDATE && !selectedRowKeys.map((k) => Number(k)).includes(Number(record.id))),
    }),
  }

  return (
    <PageShell>
      <PageHero
        title={t('PendingPOD:header.title', 'POD Pending Reports')}
        subtitle={t('PendingPOD:header.description', 'Review and approve or reject pending POD documents')}
        badges={[{ label: t('ReportsPOD:status.Pending', 'Pending'), accent: true, icon: ClipboardCheck }]}
        actions={
          canApprove ? (
            <div className="flex flex-col items-end gap-1">
              <div className="flex items-center gap-2">
                {selectedRowKeys.length > 0 && (
                  <UiButton
                    variant={showOnlySelected ? 'default' : 'outline'}
                    size="sm"
                    onClick={() => setShowOnlySelected((prev) => !prev)}
                  >
                    {showOnlySelected
                      ? t('PendingPOD:actions.showAll', 'Show All')
                      : t('PendingPOD:actions.showSelected', 'Show Selected Only ({{count}})', { count: selectedRowKeys.length })}
                  </UiButton>
                )}
                {selectedRowKeys.length > 0 && (
                  <UiButton
                    variant="outline"
                    size="sm"
                    onClick={() => {
                      setSelectedRowKeys([])
                      setShowOnlySelected(false)
                    }}
                  >
                    {t('PendingPOD:actions.clearSelection', 'Clear Selection')}
                  </UiButton>
                )}
                <UiButton onClick={handleBulkUpdate} disabled={selectedRowKeys.length === 0}>
                  {t('PendingPOD:actions.bulkUpdate', 'Bulk Update ({{count}})', { count: selectedRowKeys.length })}
                </UiButton>
              </div>
              <span className="text-xs text-muted-foreground">
                {t('PendingPOD:actions.bulkMaxHint', 'You can select up to {{max}} at a time', { max: MAX_BULK_UPDATE })}
              </span>
            </div>
          ) : undefined
        }
      />

      {!canApprove && (
        <Alert
          type="info"
          showIcon
          message="View-Only Mode"
          description="You have read-only access to pending documents. Approving or rejecting documents is restricted."
          className="mb-4"
        />
      )}

      <DashboardPanel
        title={t('PendingPOD:table.tableTitle', 'Documents')}
        subtitle={t('PendingPOD:table.tableSubtitle', 'Pending review — click any row for details')}
        flush
        className="leap-data-page-card"
      >
        <LeapDataTable<Approval>
          dataSource={displayedRows}
          columns={columns}
          rowKey="id"
          loading={isLoading || isFetching}
          rowSelection={canApprove ? rowSelection : undefined}
          rowClassName={record => {
            const isSelected = selectedRowKeys.includes(record.id)
            return `${approvalRowClass(record.status)} ${isSelected ? 'bg-primary/10 font-medium' : ''}`
          }}
          pagination={{
            ...leapTablePagination(displayedRows.length),
            current: currentPage,
          }}
          onChange={handleTableChange}
          onRow={record => ({
            onClick: e => {
              if (isTableChromeTarget(e.target)) return
              goToApproval(record)
            },
            style: { cursor: 'pointer' },
          })}
        />
      </DashboardPanel>

      <Modal
        title={
          decisionMode === 'Approved'
            ? t('PendingPOD:modal.approveTitle', 'Approve Document')
            : t('PendingPOD:modal.rejectTitle', 'Reject Document')
        }
        open={approvalModalVisible}
        onCancel={() => {
          setApprovalModalVisible(false)
          setSelectedApproval(null)
          form.resetFields()
        }}
        footer={null}
        width={600}
        destroyOnClose
      >
        {selectedApproval && (
          <Form form={form} layout="vertical" onFinish={handleApprovalSubmit}>
            <div className="mb-4 p-4 bg-muted/30 rounded-lg border border-border">
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 text-sm">
                <div>
                  <Text strong>{t('ReportsPOD:columns.documentNo', 'Document No')}:</Text> {selectedApproval.documentNo}
                </div>
                <div>
                  <Text strong>{t('ReportsPOD:columns.docType', 'Doc Type')}:</Text> <Tag color="blue">{selectedApproval.type}</Tag>
                </div>
                 <div>
                  <Text strong>{t('ReportsPOD:columns.customerName', 'Customer')}:</Text> {selectedApproval.customerName}
                </div>
                <div>
                  <Text strong>{t('ReportsPOD:columns.quantity', 'Quantity')}:</Text> {selectedApproval.quantity}
                </div>
              </div>
            </div>

            <Form.Item
              name="reason"
              label={t('PendingPOD:form.reasonLabel', 'Reason')}
              rules={[{ required: true, message: t('PendingPOD:form.reasonRequired', 'Please select a reason') }]}
            >
              <Select placeholder={t('PendingPOD:form.reasonPlaceholder', 'Select a reason')}>
                {reasonsFor(decisionMode).map((reason) => (
                  <Option key={reason} value={reason}>
                    {reason}
                  </Option>
                ))}
              </Select>
            </Form.Item>

            <Form.Item
              name="remarks"
              label={t('PendingPOD:form.remarksLabel', 'Remarks')}
              rules={
                remarksRequired
                  ? [{ required: true, message: t('PendingPOD:form.remarksRequired', 'Please provide remarks') }]
                  : []
              }
            >
              <TextArea rows={4} placeholder={t('PendingPOD:form.remarksPlaceholder', 'Enter approval or rejection remarks...')} />
            </Form.Item>

            <div className="flex justify-end space-x-2">
              <Button onClick={() => setApprovalModalVisible(false)}>{t('Common:general.cancel', 'Cancel')}</Button>
              <Button
                type="primary"
                danger={decisionMode === 'Rejected'}
                htmlType="submit"
                loading={updateApprovalMutation.isPending}
              >
                {decisionMode === 'Approved'
                  ? t('PendingPOD:actions.actionApprove', 'Approve')
                  : t('PendingPOD:actions.actionReject', 'Reject')}
              </Button>
            </div>
          </Form>
        )}
      </Modal>

      <Modal
        title={t('PendingPOD:modal.bulkTitle', 'Bulk Update ({{count}} documents)', { count: selectedRowKeys.length })}
        open={bulkModalVisible}
        onCancel={() => {
          setBulkModalVisible(false)
          bulkForm.resetFields()
        }}
        footer={null}
        width={500}
        destroyOnClose
      >
        <Form form={bulkForm} layout="vertical" onFinish={handleBulkUpdateSubmit}>
          <Form.Item
            name="status"
            label={t('PendingPOD:form.actionLabel', 'Action')}
            rules={[{ required: true, message: t('PendingPOD:form.actionRequired', 'Please select an action') }]}
          >
            <Select placeholder={t('PendingPOD:form.actionPlaceholder', 'Select action')}>
              <Option value="Approved">
                <CheckOutlined /> {t('PendingPOD:actions.approveAll', 'Approve All')}
              </Option>
              <Option value="Rejected">
                <CloseOutlined /> {t('PendingPOD:actions.rejectAll', 'Reject All')}
              </Option>
            </Select>
          </Form.Item>

          {watchedBulkStatus && (
            <Form.Item
              name="reason"
              label={t('PendingPOD:form.reasonLabel', 'Reason')}
              rules={[{ required: true, message: t('PendingPOD:form.reasonRequired', 'Please select a reason') }]}
            >
              <Select placeholder={t('PendingPOD:form.reasonPlaceholder', 'Select a reason')}>
                {reasonsFor(watchedBulkStatus as DecisionStatus).map((reason) => (
                  <Option key={reason} value={reason}>
                    {reason}
                  </Option>
                ))}
              </Select>
            </Form.Item>
          )}

          <Form.Item
            name="remarks"
            label={t('PendingPOD:form.remarksLabel', 'Remarks')}
            rules={
              bulkRemarksRequired
                ? [{ required: true, message: t('PendingPOD:form.remarksRequired', 'Please provide remarks') }]
                : []
            }
          >
            <TextArea rows={4} placeholder={t('PendingPOD:form.bulkRemarksPlaceholder', 'Enter remarks for all selected documents...')} />
          </Form.Item>

          <div className="flex justify-end space-x-2">
            <Button onClick={() => setBulkModalVisible(false)}>{t('Common:general.cancel', 'Cancel')}</Button>
            <Button type="primary" htmlType="submit" loading={bulkUpdateMutation.isPending}>
              {t('PendingPOD:actions.updateAll', 'Update All')}
            </Button>
          </div>
        </Form>
      </Modal>

      <Modal
        title={t('PendingPOD:modal.viewTitle', 'View document')}
        open={viewModalOpen}
        onCancel={() => {
          setViewModalOpen(false)
          setViewUrl(null)
          setViewRecord(null)
        }}
        footer={null}
        width="min(1200px, 98vw)"
        destroyOnClose
      >
        {viewUrl ? (
          <div className="flex flex-col gap-4 md:flex-row md:items-start">
            {viewRecord ? <DocumentPreviewSummary record={viewRecord} /> : null}
            <div className="min-h-0 min-w-0 flex-1">
              {isPdf ? (
                <iframe src={viewUrl} className="w-full" style={{ height: '75vh', border: 0 }} title="Attachment" />
              ) : (
                <img src={viewUrl} alt="attachment" className="max-h-[75vh] w-full rounded object-contain" />
              )}
            </div>
          </div>
        ) : (
          <div className="text-sm text-muted-foreground">{t('PendingPOD:modal.noAttachment', 'No attachment to preview.')}</div>
        )}
      </Modal>
    </PageShell>
  )
}

export default PendingPODPage
